import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'
import { StatusChip } from '../atoms'

const TestResultsDialog = ({
  open,
  onClose,
  testResults,
  automationName
}) => {
  if (!testResults) return null

  const totalCampaigns = testResults.length
  const triggeredCampaigns = testResults.filter(result => result.would_trigger).length
  const noTriggerCampaigns = totalCampaigns - triggeredCampaigns
  const notFoundCampaigns = testResults.filter(result => 
    result.reason && result.reason.includes('Campaign not found')
  ).length

  const getStatusIcon = (triggered) => {
    return triggered 
      ? <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#4caf50', marginRight: 8 }} />
      : <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#2196f3', marginRight: 8 }} />
  }

  const getStatusChip = (triggered) => {
    return (
      <StatusChip
        label={triggered ? 'Would Trigger' : 'No Trigger'}
        color={triggered ? 'warning' : 'neutral'}
        size="small"
        variant="outlined"
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' } }}
    >
      <DialogTitle style={{ color: 'var(--color-text-primary)' }}>
        <Box display="flex" alignItems="center">
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8, color: '#ff9800' }} />
          Test Results - {automationName}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" paragraph>
            This test shows which campaigns would trigger the automation based on their current usage.
            No actual emails were sent during this test.
          </Typography>
        </Box>

        {/* Summary Section */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Test Summary
          </Typography>
          
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" style={{ backgroundColor: 'var(--color-bg-gray)', borderColor: 'var(--color-border-light)' }}>
                <CardContent style={{ padding: 12, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {totalCampaigns}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'var(--color-text-secondary)' }}>
                    Total Campaigns
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Card variant="outlined" style={{ backgroundColor: 'var(--color-bg-gray)', borderColor: 'var(--color-border-light)' }}>
                <CardContent style={{ padding: 12, textAlign: 'center' }}>
                  <Typography variant="h4" style={{ color: '#4caf50' }} gutterBottom>
                    {triggeredCampaigns}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'var(--color-text-secondary)' }}>
                    Would Trigger
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Card variant="outlined" style={{ backgroundColor: 'var(--color-bg-gray)', borderColor: 'var(--color-border-light)' }}>
                <CardContent style={{ padding: 12, textAlign: 'center' }}>
                  <Typography variant="h4" style={{ color: '#2196f3' }} gutterBottom>
                    {noTriggerCampaigns}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'var(--color-text-secondary)' }}>
                    No Trigger
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Card variant="outlined" style={{ backgroundColor: 'var(--color-bg-gray)', borderColor: 'var(--color-border-light)' }}>
                <CardContent style={{ padding: 12, textAlign: 'center' }}>
                  <Typography variant="h4" style={{ color: '#ff9800' }} gutterBottom>
                    {notFoundCampaigns}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'var(--color-text-secondary)' }}>
                    Not Found
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {triggeredCampaigns > 0 && (
            <Box p={2} style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)', border: '1px solid rgba(22, 163, 74, 0.2)', borderRadius: 4, marginBottom: 16 }}>
              <Typography variant="body2" style={{ color: '#4caf50' }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 8 }} />
                <strong>{triggeredCampaigns} campaigns</strong> would trigger email notifications at the current threshold.
              </Typography>
            </Box>
          )}

          {triggeredCampaigns === 0 && (
            <Box p={2} style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: 4, marginBottom: 16 }}>
              <Typography variant="body2" style={{ color: 'var(--color-light-blue)' }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 8 }} />
                No campaigns would trigger at the current threshold. Consider lowering the threshold if you want more campaigns to receive alerts.
              </Typography>
            </Box>
          )}
        </Box>

        {testResults.length === 0 ? (
          <Box textAlign="center" p={3}>
            <Typography variant="body1" color="textSecondary">
              No campaigns would trigger this automation at the current threshold.
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Campaign Test Results ({testResults.length} campaigns checked)
              </Typography>
              <Divider />
            </Box>
            
            <List>
              {testResults.map((result, index) => (
                <ListItem key={result.campaign_name || index} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                          {getStatusIcon(result.would_trigger)}
                          <Typography variant="subtitle1">
                            {result.campaign_name}
                          </Typography>
                        </Box>
                        {getStatusChip(result.would_trigger)}
                      </Box>
                    }
                    secondary={
                      <Box mt={1}>
                        <Typography variant="body2" color="textSecondary">
                          Usage: {result.metrics?.current_month_minutes || 0} of {result.metrics?.plan_minutes || 0} minutes 
                          ({result.metrics?.plan_used_percent || 0}% used)
                        </Typography>
                        {result.would_trigger && (
                          <Typography variant="body2" color="secondary" style={{ marginTop: 4 }}>
                            ✓ Would trigger at {result.trigger_threshold}% threshold
                          </Typography>
                        )}
                        {result.reason && (
                          <Typography variant="body2" style={{ marginTop: 4, fontStyle: 'italic' }}>
                            {result.reason}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained" style={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

TestResultsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  testResults: PropTypes.array,
  automationName: PropTypes.string
}

export default TestResultsDialog